import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  Snackbar,
  Card,
  CardContent,
  Divider,
  Avatar,
  useMediaQuery,
  useTheme,
  Menu,
  ListItemIcon,
  ListItemText,
  Autocomplete
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Add as AddIcon,
  Tv as TvIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PhotoLibrary as PhotoLibraryIcon,
  Close as CloseIcon,
  Business as BusinessIcon,
  ContentCopy as ContentCopyIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Restaurant as RestaurantIcon,
  Image as ImageIcon,
  TouchApp as KioskIcon,
  ScreenRotation as ScreenRotationIcon,
  PointOfSale as PavoIcon,
  MeetingRoom as AccessControlIcon,
  Storefront as SalesTerminalIcon,
  Fingerprint as PdksIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ownAuthSession from '../../services/auth/ownAuthSession';
import { useAuth } from '../../contexts/AuthContext';
import { mainApi } from '../../config/apiClient';
import MediaManagementModal from './MediaManagementModal';
import { pavoApiService } from '../../services/pavoApiService';
import salesService, { QuickSaleScreenDto } from '../../services/salesService';
import { restaurantScreenApi, RestaurantScreenDto } from '../../services/restaurantApiService';
import { TerminalDeviceMode } from '../../services/auth/terminalSession';
interface Branch {
  id: string;
  name: string;
  address: string;
  phone: string;
  createdAt: number;
}

interface QRMenu {
  id: string;
  name: string;
  branchId?: string;
  branchName?: string;
}

interface KioskItem {
  id: number;
  name: string;
  branchId?: string;
  branchName?: string;
}

/** Satış terminali modalındaki ekran seçicisinin dört kaynağı için ortak biçim. */
interface SalesScreenOption {
  id: number;
  name: string;
  branchId?: string | null;
}

interface DeviceScreen {
  id: number;
  screenCode: string;
  screenName: string;
  description?: string;
  screenType: 'media' | 'orders' | 'kiosk' | 'pavo' | 'accessControl' | 'salesTerminal' | 'pdks';
  deviceMode?: string;
  linkedScreenId?: number;
  orientation?: string;
  branchId?: string;
  branchName?: string;
  qrMenuId?: string;
  qrMenuName?: string;
  kioskId?: number;
  status: 'pending' | 'active' | 'inactive';
  macAddress?: string;
  deviceModel?: string;
  androidVersion?: string;
  appVersion?: string;
  lastSeen?: string;
  activatedAt?: string;
  mediaSettings?: string;
  createdAt: string;
  isActive: boolean;
}

const DeviceManagement: React.FC = () => {
  const { t, i18n } = useTranslation('common2');
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 1499px)');
  const isEnglish = i18n.language?.toLowerCase().startsWith('en');
  const tr = (trText: string, enText: string) => (isEnglish ? enText : trText);
  
  // ✅ AuthContext'ten merkezi cache kullan (Firebase listener yerine)
  const { userDetails, branches: authBranches, loadBranches } = useAuth();
  const companyId = userDetails?.companyId || null;
  
  // Şubeleri Branch tipine dönüştür
  const branches: Branch[] = authBranches.filter(b => b.id !== 'null').map(b => ({
    id: b.id,
    name: b.name,
    address: b.address || '',
    phone: b.phone || '',
    createdAt: 0
  }));
  
  // Sayfa üstündeki şube filtresi. Yalnızca listeyi süzer; modallar bu değeri ASLA
  // değiştirmez, aksi halde cihaz eklendikten sonra kullanıcı bulunduğu şubeden düşer.
  const [selectedBranchId, setSelectedBranchId] = useState<string>('');
  // Ekleme/düzenleme formlarındaki şube alanı. Filtreden bağımsızdır ve opsiyoneldir:
  // bir cihaz şubeye değil doğrudan şirket merkezine bağlı olabilir.
  const [formBranchId, setFormBranchId] = useState<string>('');
  const [qrMenus, setQrMenus] = useState<QRMenu[]>([]); // ✅ YENİ
  
  // Ekranlar
  const [screens, setScreens] = useState<DeviceScreen[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Modal states
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addOrdersModalOpen, setAddOrdersModalOpen] = useState(false);
  const [addKioskModalOpen, setAddKioskModalOpen] = useState(false);
  const [addAccessControlModalOpen, setAddAccessControlModalOpen] = useState(false);
  const [addPdksModalOpen, setAddPdksModalOpen] = useState(false);
  const [mediaModalOpen, setMediaModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedScreen, setSelectedScreen] = useState<DeviceScreen | null>(null);
  
  // Kiosk listesi
  const [kiosks, setKiosks] = useState<KioskItem[]>([]);
  const [selectedKioskId, setSelectedKioskId] = useState<string>('');

  // Pavo states
  const [addPavoModalOpen, setAddPavoModalOpen] = useState(false);
  const [pavoDeviceMode, setPavoDeviceMode] = useState<string>('pavo_order');
  const [pavoDeviceName, setPavoDeviceName] = useState('');
  const [pavoLinkedScreenId, setPavoLinkedScreenId] = useState<number | ''>('');
  const [quickSaleScreensForPavo, setQuickSaleScreensForPavo] = useState<QuickSaleScreenDto[]>([]);
  const [restaurantScreensForPavo, setRestaurantScreensForPavo] = useState<RestaurantScreenDto[]>([]);
  const [loadingScreensForPavo, setLoadingScreensForPavo] = useState(false);

  // Satış terminali (11 haneli kod) states. Şube alanı yok: cihazın şubesi
  // bağlanan satış ekranından devralınıyor (DeviceController.AddDeviceScreen).
  const [addSalesTerminalModalOpen, setAddSalesTerminalModalOpen] = useState(false);
  const [salesTerminalMode, setSalesTerminalMode] = useState<TerminalDeviceMode>('sales_quicksale');
  const [salesTerminalName, setSalesTerminalName] = useState('');
  const [salesTerminalLinkedScreenId, setSalesTerminalLinkedScreenId] = useState<number | ''>('');
  const [salesTerminalScreenOptions, setSalesTerminalScreenOptions] = useState<SalesScreenOption[]>([]);
  const [loadingSalesTerminalScreens, setLoadingSalesTerminalScreens] = useState(false);
  
  // Form states
  const [screenName, setScreenName] = useState('');
  const [screenDescription, setScreenDescription] = useState('');
  const [screenOrientation, setScreenOrientation] = useState('landscape');
  const [selectedMenuId, setSelectedMenuId] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');
  
  // 3-nokta menü state
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedScreenForMenu, setSelectedScreenForMenu] = useState<DeviceScreen | null>(null);

  // Sağ üstteki "Cihaz Ekle" açılır menüsü
  const [addMenuAnchorEl, setAddMenuAnchorEl] = useState<null | HTMLElement>(null);

  // Düzenleme state
  const [editingScreen, setEditingScreen] = useState<DeviceScreen | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, screen: DeviceScreen) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedScreenForMenu(screen);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedScreenForMenu(null);
  };

  const openEditModal = (screen: DeviceScreen) => {
    console.log('[EDIT] Düzenleme açılıyor:', screen);
    console.log('[EDIT] screen.orientation:', screen.orientation);
    setEditingScreen(screen);
    setScreenName(screen.screenName);
    setScreenDescription(screen.description || '');
    setScreenOrientation(screen.orientation || 'landscape');
    console.log('[EDIT] screenOrientation set edildi:', screen.orientation || 'landscape');
    if (screen.screenType === 'media') {
      setFormBranchId(screen.branchId || '');
      setAddModalOpen(true);
    } else if (screen.screenType === 'orders') {
      setSelectedMenuId(screen.qrMenuId || '');
      setAddOrdersModalOpen(true);
    }
  };

  const resetFormAndCloseModals = () => {
    setScreenName('');
    setScreenDescription('');
    setScreenOrientation('landscape');
    setEditingScreen(null);
    setFormBranchId('');
    setSelectedMenuId('');
    setAddModalOpen(false);
    setAddOrdersModalOpen(false);
    setAddKioskModalOpen(false);
    setAddAccessControlModalOpen(false);
    setAddPdksModalOpen(false);
    setAddPavoModalOpen(false);
    setPavoDeviceMode('pavo_order');
    setPavoDeviceName('');
    setPavoLinkedScreenId('');
    setAddSalesTerminalModalOpen(false);
    setSalesTerminalMode('sales_quicksale');
    setSalesTerminalName('');
    setSalesTerminalLinkedScreenId('');
  };

  // Component mount - Şubeleri merkezi cache'den yükle
  useEffect(() => {
    const initializeData = async () => {
      try {
        setLoading(true);
        
        // AuthContext'te şubeler yoksa yükle
        if (companyId && branches.length === 0) {
          await loadBranches();
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Veri yüklenirken hata:', error);
        showSnackbar('Veri yüklenirken bir hata oluştu.', 'error');
        setLoading(false);
      }
    };

    if (companyId) {
      initializeData();
    } else {
      setLoading(false);
    }
  }, [companyId]);

  // Ekranları yükle. Şube filtresi boşsa şirketin tüm cihazları (şubesizler dahil) listelenir.
  useEffect(() => {
    if (companyId) {
      loadScreens();
      loadQRMenus(); // ✅ YENİ
    }
  }, [companyId, selectedBranchId]);

  // Pavo cihaz ekleme modalı icin QuickSale / Restaurant ekranlarini yukle
  useEffect(() => {
    if (!addPavoModalOpen) return;
    if (pavoDeviceMode !== 'pavo_quicksale' && pavoDeviceMode !== 'pavo_restaurant') return;
    if (!companyId) return;

    let cancelled = false;
    const loadScreensForMode = async () => {
      try {
        setLoadingScreensForPavo(true);
        if (pavoDeviceMode === 'pavo_quicksale') {
          const list = await salesService.getQuickSaleScreens();
          if (cancelled) return;
          const filtered = list.filter(
            (s) => s.IsActive && (!formBranchId || !s.BranchId || s.BranchId === formBranchId),
          );
          setQuickSaleScreensForPavo(filtered);
        } else {
          const list = await restaurantScreenApi.getByCompanyId();
          if (cancelled) return;
          const filtered = list.filter(
            (s) => s.IsActive && (!formBranchId || !s.BranchId || s.BranchId === formBranchId),
          );
          setRestaurantScreensForPavo(filtered);
        }
      } catch (err) {
        console.error('Pavo icin ekran listesi yuklenemedi:', err);
      } finally {
        if (!cancelled) setLoadingScreensForPavo(false);
      }
    };
    loadScreensForMode();
    return () => {
      cancelled = true;
    };
  }, [addPavoModalOpen, pavoDeviceMode, companyId, formBranchId]);

  // Cihaz modu degistiginde secili ekrani sifirla
  useEffect(() => {
    setPavoLinkedScreenId('');
  }, [pavoDeviceMode]);

  /**
   * Satış terminali modalı için hedef moda ait ekran listesini yükler.
   * Liste sayfa üstündeki şube filtresine göre süzülür: kullanıcı bir şubeyi
   * süzüyorsa büyük olasılıkla o şubenin ekranına kod üretiyor. Şubesiz
   * (doğrudan şirkete bağlı) ekranlar her filtrede görünür.
   */
  useEffect(() => {
    if (!addSalesTerminalModalOpen) return;
    if (!companyId) return;

    let cancelled = false;
    const inSelectedBranch = (branchId?: string | null) =>
      !selectedBranchId || !branchId || branchId === selectedBranchId;

    const loadScreensForMode = async () => {
      try {
        setLoadingSalesTerminalScreens(true);
        let options: SalesScreenOption[] = [];

        switch (salesTerminalMode) {
          case 'sales_quicksale': {
            const list = await salesService.getQuickSaleScreens();
            options = list
              .filter((s) => s.IsActive && inSelectedBranch(s.BranchId))
              .map((s) => ({ id: s.Id, name: s.Name, branchId: s.BranchId }));
            break;
          }
          case 'sales_restaurant': {
            // Kod RestaurantScreens.Id'ye bağlanır; /restaurant-screen/:setupId
            // rotası bu Id'yi bekliyor, bağlı QuickSaleScreenId'yi değil.
            const list = await restaurantScreenApi.getByCompanyId();
            options = list
              .filter((s) => s.IsActive && inSelectedBranch(s.BranchId))
              .map((s) => ({ id: s.Id, name: s.Name, branchId: s.BranchId }));
            break;
          }
          case 'sales_package': {
            const list = await salesService.getPackageScreens();
            options = list
              .filter((s) => s.IsActive && inSelectedBranch(s.BranchId))
              .map((s) => ({ id: s.Id, name: s.Name, branchId: s.BranchId }));
            break;
          }
          case 'sales_pyk': {
            const list = await salesService.getPykScreens();
            options = list
              .filter((s) => s.IsActive && inSelectedBranch(s.BranchId))
              .map((s) => ({ id: s.Id, name: s.Name, branchId: s.BranchId }));
            break;
          }
        }

        if (cancelled) return;
        setSalesTerminalScreenOptions(options);
      } catch (err) {
        console.error('Satış terminali için ekran listesi yüklenemedi:', err);
        if (!cancelled) setSalesTerminalScreenOptions([]);
      } finally {
        if (!cancelled) setLoadingSalesTerminalScreens(false);
      }
    };

    loadScreensForMode();
    return () => {
      cancelled = true;
    };
  }, [addSalesTerminalModalOpen, salesTerminalMode, companyId, selectedBranchId]);

  // Hedef mod degistiginde secili ekrani sifirla
  useEffect(() => {
    setSalesTerminalLinkedScreenId('');
  }, [salesTerminalMode]);

  const loadQRMenus = async () => {
    if (!companyId) return;

    try {
      // Backend: GET /api/QRMenu/menu-definitions (companyId X-Company-Id header'dan alınır)
      const response = await mainApi.get(`/api/QRMenu/menu-definitions`);

      if (response.data.success && response.data.data) {
        // Seçili şubeye ait menüleri filtrele
        const menusForBranch = response.data.data.filter(
          (menu: any) => !selectedBranchId || menu.branchId === selectedBranchId
        );
        setQrMenus(menusForBranch.map((menu: any) => ({
          id: menu.id,
          name: menu.name,
          branchId: menu.branchId,
          branchName: menu.branchName
        })));
      }
    } catch (error) {
      console.error('QR menüleri yüklenirken hata:', error);
    }
  };

  // Kiosk listesini yükle
  const loadKiosks = async () => {
    if (!companyId) return;
    try {
      const response = await mainApi.get(`/api/Kiosk`);
      if (response.data.success && response.data.data) {
        setKiosks(response.data.data.map((k: any) => ({
          id: k.id,
          name: k.name,
          branchId: k.branchId,
          branchName: k.branchName
        })));
      }
    } catch (error) {
      console.error('Kiosk listesi yüklenirken hata:', error);
    }
  };

  // Component mount'ta kiosks yükle
  useEffect(() => {
    if (companyId) {
      loadKiosks();
    }
  }, [companyId]);

  const loadScreens = async () => {
    if (!companyId) return;

    try {
      setRefreshing(true);
      const response = await mainApi.get(
        `/api/device/list`,
        {
          // branchId yalnızca filtre seçiliyse gönderilir; backend parametre gelmezse
          // şirketin tüm cihazlarını döndürür (DeviceController.GetDeviceScreens).
          params: selectedBranchId ? { companyId, branchId: selectedBranchId } : { companyId },
        }
      );

      if (response.data.success) {
        setScreens(response.data.screens || []);
      }
    } catch (error) {
      console.error('Ekranlar yüklenirken hata:', error);
      showSnackbar('Ekranlar yüklenirken bir hata oluştu.', 'error');
    } finally {
      setRefreshing(false);
    }
  };

  const handleAddScreen = async () => {
    if (!screenName.trim()) {
      showSnackbar('Ekran adı zorunludur.', 'warning');
      return;
    }

    if (!companyId) {
      showSnackbar('Şirket bilgisi eksik.', 'error');
      return;
    }

    try {
      setSaving(true);
      const selectedBranch = branches.find(b => b.id === formBranchId);

      const response = await mainApi.post(
        `/api/device/add`,
        {
          companyId,
          screenType: 'media',
          branchId: formBranchId || null,
          branchName: selectedBranch?.name || null,
          screenName: screenName.trim(),
          description: screenDescription.trim() || null,
          orientation: screenOrientation,
          createdBy: ownAuthSession.getCurrentUid()
        }
      );

      if (response.data.success) {
        showSnackbar(`Medya ekranı başarıyla eklendi!${filterMismatchNote()}`, 'success');
        resetFormAndCloseModals();
        loadScreens();
      }
    } catch (error: any) {
      console.error('Ekran eklenirken hata:', error);
      showSnackbar(error.response?.data?.message || 'Ekran eklenirken bir hata oluştu.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ✅ YENİ FONKS İYON - Sipariş ekranı ekle
  const handleAddOrdersScreen = async () => {
    if (!screenName.trim()) {
      showSnackbar('Ekran adı zorunludur.', 'warning');
      return;
    }

    if (!selectedMenuId) {
      showSnackbar('Menü seçimi zorunludur.', 'warning');
      return;
    }

    if (!companyId) {
      showSnackbar('Şirket bilgisi eksik.', 'error');
      return;
    }

    try {
      setSaving(true);
      const selectedMenu = qrMenus.find(m => m.id === selectedMenuId);

      const response = await mainApi.post(
        `/api/device/add`,
        {
          companyId,
          screenType: 'orders',
          // Sipariş ekranının şubesi seçilen QR menüden gelir; menü şubesizse cihaz da şubesizdir.
          branchId: selectedMenu?.branchId || null,
          branchName: selectedMenu?.branchName || null,
          qrMenuId: selectedMenuId,
          qrMenuName: selectedMenu?.name,
          screenName: screenName.trim(),
          description: screenDescription.trim() || null,
          orientation: screenOrientation,
          createdBy: ownAuthSession.getCurrentUid()
        }
      );

      if (response.data.success) {
        showSnackbar('Sipariş ekranı başarıyla eklendi!', 'success');
        resetFormAndCloseModals();
        loadScreens();
      }
    } catch (error: any) {
      console.error('Sipariş ekranı eklenirken hata:', error);
      showSnackbar(error.response?.data?.message || 'Sipariş ekranı eklenirken bir hata oluştu.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Kiosk ekranı ekle
  const handleAddKioskScreen = async () => {
    if (!screenName.trim()) {
      showSnackbar('Ekran adı zorunludur.', 'warning');
      return;
    }

    if (!selectedKioskId) {
      showSnackbar('Kiosk seçimi zorunludur.', 'warning');
      return;
    }

    if (!companyId) {
      showSnackbar('Şirket bilgisi eksik.', 'error');
      return;
    }

    try {
      setSaving(true);

      const response = await mainApi.post(
        `/api/device/add`,
        {
          companyId,
          screenType: 'kiosk',
          kioskId: parseInt(selectedKioskId),
          screenName: screenName.trim(),
          description: screenDescription.trim() || null,
          orientation: screenOrientation,
          createdBy: ownAuthSession.getCurrentUid()
        }
      );

      if (response.data.success) {
        showSnackbar('Kiosk ekranı başarıyla eklendi!', 'success');
        resetFormAndCloseModals();
        setSelectedKioskId('');
        loadScreens();
      }
    } catch (error: any) {
      console.error('Kiosk ekranı eklenirken hata:', error);
      showSnackbar(error.response?.data?.message || 'Kiosk ekranı eklenirken bir hata oluştu.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Geçiş kontrol bilgisayarı ekle (fabrika kapı geçiş kontrolü)
  const handleAddAccessControlScreen = async () => {
    if (!screenName.trim()) {
      showSnackbar(tr('Cihaz adı zorunludur.', 'Device name is required.'), 'warning');
      return;
    }

    if (!companyId) {
      showSnackbar(tr('Şirket bilgisi eksik.', 'Company information is missing.'), 'error');
      return;
    }

    try {
      setSaving(true);
      const selectedBranch = branches.find(b => b.id === formBranchId);

      const response = await mainApi.post(
        `/api/device/add`,
        {
          companyId,
          screenType: 'accessControl',
          branchId: formBranchId || null,
          branchName: selectedBranch?.name || null,
          screenName: screenName.trim(),
          description: screenDescription.trim() || null,
          createdBy: ownAuthSession.getCurrentUid()
        }
      );

      if (response.data.success) {
        showSnackbar(
          tr('Geçiş kontrol bilgisayarı eklendi! Kodu local-service üzerinde girin.', 'Access control computer added. Enter the code in local-service.') + filterMismatchNote(),
          'success'
        );
        resetFormAndCloseModals();
        loadScreens();
      }
    } catch (error: any) {
      console.error('Geçiş kontrol bilgisayarı eklenirken hata:', error);
      showSnackbar(error.response?.data?.message || tr('Geçiş kontrol bilgisayarı eklenirken bir hata oluştu.', 'An error occurred while adding the access control computer.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAddPdksScreen = async () => {
    if (!screenName.trim()) {
      showSnackbar(tr('Cihaz adı zorunludur.', 'Device name is required.'), 'warning');
      return;
    }
    if (!companyId) {
      showSnackbar(tr('Şirket bilgisi eksik.', 'Company information is missing.'), 'error');
      return;
    }
    try {
      setSaving(true);
      const selectedBranch = branches.find(b => b.id === formBranchId);
      const response = await mainApi.post('/api/device/add', {
        companyId,
        screenType: 'pdks',
        branchId: formBranchId || null,
        branchName: selectedBranch?.name || null,
        screenName: screenName.trim(),
        description: screenDescription.trim() || null,
        createdBy: ownAuthSession.getCurrentUid()
      });
      if (response.data.success) {
        showSnackbar(
          tr('PDKS bilgisayarı eklendi. Kodu local-service /api/pdks/pair ile girin.', 'PDKS computer added. Enter the code via local-service /api/pdks/pair.') + filterMismatchNote(),
          'success'
        );
        resetFormAndCloseModals();
        loadScreens();
      }
    } catch (error: any) {
      showSnackbar(error.response?.data?.message || tr('PDKS ekranı eklenemedi.', 'Could not add PDKS screen.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Ekran tipinin çip görünümü. Tek yerde tutuluyor: masaüstü tablosu ile mobil kart
   * görünümü ayrı ayrı üçlü koşul zincirleri kullandığı için kiosk mobilde yanlışlıkla
   * "Sipariş" olarak görünüyordu.
   */
  const getScreenTypeMeta = (screenType: DeviceScreen['screenType']) => {
    switch (screenType) {
      case 'media':
        return { icon: <ImageIcon fontSize="small" />, label: tr('Medya', 'Media'), color: 'primary' as const, borderColor: 'primary.main' };
      case 'kiosk':
        return { icon: <KioskIcon fontSize="small" />, label: tr('Kiosk', 'Kiosk'), color: 'warning' as const, borderColor: 'warning.main' };
      case 'pavo':
        return { icon: <PavoIcon fontSize="small" />, label: 'PavoPay', color: 'info' as const, borderColor: 'info.main' };
      case 'accessControl':
        return { icon: <AccessControlIcon fontSize="small" />, label: tr('Geçiş Kontrol', 'Access Control'), color: 'success' as const, borderColor: 'success.main' };
      case 'pdks':
        return { icon: <PdksIcon fontSize="small" />, label: 'PDKS', color: 'secondary' as const, borderColor: 'secondary.main' };
      case 'salesTerminal':
        return { icon: <SalesTerminalIcon fontSize="small" />, label: tr('Satış Ekranı', 'Sales Screen'), color: 'default' as const, borderColor: 'grey.500' };
      default:
        return { icon: <RestaurantIcon fontSize="small" />, label: tr('Sipariş', 'Orders'), color: 'secondary' as const, borderColor: 'secondary.main' };
    }
  };

  /** Satış terminali kayıtlarındaki DeviceMode değerinin okunur karşılığı. */
  const getSalesTerminalModeLabel = (deviceMode?: string) => {
    switch (deviceMode) {
      case 'sales_quicksale':
        return tr('Hızlı Satış', 'Quick Sale');
      case 'sales_restaurant':
        return tr('Restoran', 'Restaurant');
      case 'sales_package':
        return tr('Paket Sipariş', 'Package Order');
      case 'sales_pyk':
        return tr('PYK', 'PYK');
      default:
        return deviceMode || '-';
    }
  };

  const handleAddPavoScreen = async () => {
    if (!pavoDeviceName.trim()) {
      showSnackbar('Cihaz adı zorunludur.', 'warning');
      return;
    }
    if (!companyId) {
      showSnackbar('Şirket bilgisi eksik.', 'error');
      return;
    }
    if (
      (pavoDeviceMode === 'pavo_quicksale' || pavoDeviceMode === 'pavo_restaurant') &&
      !pavoLinkedScreenId
    ) {
      showSnackbar('Bu cihaz modu için bir ekran seçmelisiniz.', 'warning');
      return;
    }
    try {
      setSaving(true);
      const trimmedName = pavoDeviceName.trim();
      const linkedId =
        pavoDeviceMode === 'pavo_quicksale' || pavoDeviceMode === 'pavo_restaurant'
          ? (pavoLinkedScreenId as number)
          : undefined;

      const result = await pavoApiService.addPavoDevice(
        companyId,
        formBranchId || null,
        pavoDeviceMode,
        trimmedName,
        linkedId,
      );

      let linkedScreenName: string | undefined;
      if (pavoDeviceMode === 'pavo_quicksale' && linkedId) {
        linkedScreenName = quickSaleScreensForPavo.find((s) => s.Id === linkedId)?.Name;
      } else if (pavoDeviceMode === 'pavo_restaurant' && linkedId) {
        linkedScreenName = restaurantScreensForPavo.find((s) => s.Id === linkedId)?.Name;
      }

      const infoParts = [trimmedName];
      if (linkedScreenName) infoParts.push(`(${linkedScreenName})`);
      infoParts.push(`— Cihaz Kodu: ${result.deviceCode}`);
      showSnackbar(
        `PavoPay cihazi eklendi: ${infoParts.join(' ')}${filterMismatchNote()}`,
        'success',
      );
      resetFormAndCloseModals();
      loadScreens();
    } catch (error: any) {
      console.error('Pavo cihazı eklenirken hata:', error);
      showSnackbar(error.message || 'Pavo cihazı eklenirken bir hata oluştu.', 'error');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Satış terminali kodu üretir. Şube gönderilmez: backend cihazın şubesini
   * bağlanan satış ekranından devralıyor, buradan gönderilen değer yok sayılır.
   */
  const handleAddSalesTerminal = async () => {
    if (!salesTerminalName.trim()) {
      showSnackbar(tr('Cihaz adı zorunludur.', 'Device name is required.'), 'warning');
      return;
    }
    if (!companyId) {
      showSnackbar(tr('Şirket bilgisi eksik.', 'Company information is missing.'), 'error');
      return;
    }
    if (!salesTerminalLinkedScreenId) {
      showSnackbar(tr('Bağlanacak satış ekranını seçmelisiniz.', 'You must select the sales screen to link.'), 'warning');
      return;
    }

    try {
      setSaving(true);
      const trimmedName = salesTerminalName.trim();
      const response = await mainApi.post(`/api/device/add`, {
        companyId,
        screenType: 'salesTerminal',
        deviceMode: salesTerminalMode,
        linkedScreenId: salesTerminalLinkedScreenId,
        screenName: trimmedName,
        createdBy: ownAuthSession.getCurrentUid()
      });

      if (response.data.success) {
        const created = response.data.screen;
        const linkedName = salesTerminalScreenOptions.find((s) => s.id === salesTerminalLinkedScreenId)?.name;
        const parts = [trimmedName];
        if (linkedName) parts.push(`(${linkedName})`);
        parts.push(`— ${tr('Ekran Kodu', 'Screen Code')}: ${created?.screenCode}`);
        showSnackbar(
          `${tr('Satış ekranı kodu üretildi', 'Sales screen code generated')}: ${parts.join(' ')}${salesTerminalBranchNote(created?.branchId)}`,
          'success'
        );
        resetFormAndCloseModals();
        loadScreens();
      } else {
        showSnackbar(response.data.message || tr('Kod üretilemedi.', 'The code could not be generated.'), 'error');
      }
    } catch (error: any) {
      console.error('Satış ekranı kodu üretilirken hata:', error);
      showSnackbar(
        error.response?.data?.message || tr('Kod üretilirken bir hata oluştu.', 'An error occurred while generating the code.'),
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateScreen = async () => {
    if (!editingScreen || !companyId) return;

    try {
      setSaving(true);

      const body: any = {
        screenId: editingScreen.id,
        companyId,
        screenName: screenName.trim(),
        description: screenDescription.trim() || null,
        orientation: screenOrientation,
        updatedBy: ownAuthSession.getCurrentUid()
      };

      if (editingScreen.screenType === 'media') {
        const selectedBranch = branches.find(b => b.id === formBranchId);
        body.branchId = formBranchId || null;
        body.branchName = selectedBranch?.name || null;
      } else if (editingScreen.screenType === 'orders') {
        const selectedMenu = qrMenus.find(m => m.id === selectedMenuId);
        body.qrMenuId = selectedMenuId;
        body.qrMenuName = selectedMenu?.name;
      }

      console.log('[UPDATE-SCREEN] editingScreen:', editingScreen);
      console.log('[UPDATE-SCREEN] screenOrientation state:', screenOrientation);
      console.log('[UPDATE-SCREEN] POST body:', JSON.stringify(body, null, 2));

      const response = await mainApi.post(`/api/device/update-screen`, body);

      console.log('[UPDATE-SCREEN] Response:', response.data);

      if (response.data.success) {
        showSnackbar('Ekran başarıyla güncellendi!', 'success');
        resetFormAndCloseModals();
        loadScreens();
      } else {
        console.error('[UPDATE-SCREEN] Başarısız:', response.data.message);
        showSnackbar(response.data.message || 'Güncelleme başarısız', 'error');
      }
    } catch (error: any) {
      console.error('[UPDATE-SCREEN] Hata:', error.response?.data || error.message);
      showSnackbar(error.response?.data?.message || 'Güncelleme sırasında hata oluştu', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteScreen = async () => {
    if (!selectedScreen) return;

    // ✅ MULTI-TENANT: CompanyId kontrolü
    if (!companyId) {
      showSnackbar('Şirket bilgisi bulunamadı.', 'error');
      return;
    }

    try {
      setSaving(true);
      const response = await mainApi.post(
        `/api/device/delete`,
        {
          screenId: selectedScreen.id,
          companyId: companyId,
          deletedBy: ownAuthSession.getCurrentUid()
        }
      );

      if (response.data.success) {
        showSnackbar('Ekran başarıyla silindi.', 'success');
        setDeleteModalOpen(false);
        setSelectedScreen(null);
        loadScreens();
      }
    } catch (error: any) {
      console.error('Ekran silinirken hata:', error);
      showSnackbar(error.response?.data?.message || 'Ekran silinirken bir hata oluştu.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    showSnackbar('Ekran kodu kopyalandı!', 'success');
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'pending': return 'warning';
      case 'inactive': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircleIcon fontSize="small" />;
      case 'pending': return <PendingIcon fontSize="small" />;
      case 'inactive': return <CancelIcon fontSize="small" />;
      default: return null;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return tr('Aktif', 'Active');
      case 'pending': return tr('Kurulum Bekliyor', 'Setup Pending');
      case 'inactive': return tr('Pasif', 'Inactive');
      default: return status;
    }
  };

  /**
   * Ekleme modallarını tek yerden açar. Form alanlarını sıfırlar ve form şubesini
   * mevcut filtreden tohumlar: kullanıcı bir şubeyi süzüyorsa cihazı büyük olasılıkla
   * o şubeye ekliyor. Şube alanı boş bırakılabilir.
   */
  const openAddModal = (kind: 'media' | 'orders' | 'kiosk' | 'accessControl' | 'pdks' | 'pavo' | 'salesTerminal') => {
    setAddMenuAnchorEl(null);
    setEditingScreen(null);
    setScreenName('');
    setScreenDescription('');
    setScreenOrientation('landscape');
    setFormBranchId(selectedBranchId);

    switch (kind) {
      case 'media':
        setAddModalOpen(true);
        break;
      case 'orders':
        setSelectedMenuId('');
        setAddOrdersModalOpen(true);
        break;
      case 'kiosk':
        setSelectedKioskId('');
        setAddKioskModalOpen(true);
        break;
      case 'accessControl':
        setAddAccessControlModalOpen(true);
        break;
      case 'pdks':
        setAddPdksModalOpen(true);
        break;
      case 'pavo':
        setPavoDeviceName('');
        setPavoDeviceMode('pavo_order');
        setPavoLinkedScreenId('');
        setAddPavoModalOpen(true);
        break;
      case 'salesTerminal':
        setSalesTerminalName('');
        setSalesTerminalMode('sales_quicksale');
        setSalesTerminalLinkedScreenId('');
        setSalesTerminalScreenOptions([]);
        setAddSalesTerminalModalOpen(true);
        break;
    }
  };

  /**
   * Eklenen cihazın şubesi aktif filtreyle uyuşmuyorsa cihaz listede görünmez.
   * Kullanıcı "eklendi" mesajını görüp listede bulamamasın diye uyarı metni döner.
   */
  const filterMismatchNote = () =>
    selectedBranchId && formBranchId !== selectedBranchId
      ? tr(
          ' Cihaz farklı bir şubeye kaydedildi; görmek için şube filtresini değiştirin.',
          ' The device was saved to a different branch; change the branch filter to see it.'
        )
      : '';

  /**
   * Satış terminalinin şubesi kullanıcı tarafından seçilmiyor, bağlanan satış
   * ekranından geliyor. Devralınan şube aktif filtreyle uyuşmazsa cihaz listede
   * görünmez; bu yüzden ayrı bir uyarı metni üretiliyor.
   */
  const salesTerminalBranchNote = (createdBranchId?: string | null) =>
    selectedBranchId && (createdBranchId || '') !== selectedBranchId
      ? tr(
          ' Cihaz, bağlı ekranın şubesine kaydedildi; görmek için şube filtresini değiştirin.',
          " The device was saved to the linked screen's branch; change the branch filter to see it."
        )
      : '';

  /**
   * Modal içindeki opsiyonel şube alanı. Sayfa üstündeki filtreden bağımsızdır:
   * buradaki seçim listeyi etkilemez, yalnızca oluşturulacak cihazın şubesini belirler.
   */
  const renderFormBranchField = () => (
    <Autocomplete
      size="small"
      fullWidth
      options={branches}
      value={branches.find((b) => b.id === formBranchId) || null}
      onChange={(_, newValue) => setFormBranchId(newValue?.id || '')}
      getOptionLabel={(option) => option.name || ''}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      disabled={saving || branches.length === 0}
      noOptionsText={tr('Şube bulunamadı', 'No branches found')}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon fontSize="small" />
            {option.name}
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={tr('Şube (opsiyonel)', 'Branch (optional)')}
          size="small"
          helperText={tr(
            'Boş bırakılırsa cihaz doğrudan şirkete bağlanır.',
            'Leave empty to attach the device directly to the company.'
          )}
        />
      )}
    />
  );

  return (
    <Container maxWidth="xl" sx={{ mt: isMobile ? 2 : 4, mb: 4, px: isMobile ? 2 : 3 }}>
      {/* Header */}
      <Paper
        elevation={3}
        sx={{
          p: isMobile ? 2 : 4,
          borderRadius: 2,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          mb: isMobile ? 2 : 4
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1.5 : 2 }}>
          <TvIcon sx={{ fontSize: isMobile ? 32 : 48 }} />
          <Box>
            <Typography variant={isMobile ? 'h6' : 'h4'} component="h1" sx={{ mb: 0 }}>
              {isMobile ? tr('Cihaz Yönetimi', 'Device Management') : tr('Cihaz Yönetimi - Digital Signage', 'Device Management - Digital Signage')}
            </Typography>
            {!isMobile && (
              <Typography variant="body1" sx={{ opacity: 0.9, mt: 0.5 }}>
                {t('deviceManagement.description')}
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Şube Filtresi + Cihaz Ekle */}
      <Card sx={{ mb: isMobile ? 2 : 3 }}>
        <CardContent sx={{ p: isMobile ? 2 : 3, '&:last-child': { pb: isMobile ? 2 : 3 } }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              {/* ✅ FIX: Select -> Autocomplete, kucuk boyutlu (yari yariya daralti) */}
              <Autocomplete
                size="small"
                fullWidth
                options={branches}
                value={branches.find((b) => b.id === selectedBranchId) || null}
                onChange={(_, newValue) => setSelectedBranchId(newValue?.id || '')}
                getOptionLabel={(option) => option.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={loading || branches.length === 0}
                noOptionsText={tr('Şube bulunamadı', 'No branches found')}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BusinessIcon fontSize="small" />
                      {option.name}
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={tr('Şube Filtresi', 'Branch Filter')}
                    size="small"
                    placeholder={tr('Tüm şubeler', 'All branches')}
                  />
                )}
              />
              {branches.length === 0 && !loading && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {tr(
                    'Henüz şube eklenmemiş. Cihazlar doğrudan şirkete bağlı olarak eklenebilir.',
                    'No branches have been added yet. Devices can be added attached directly to the company.'
                  )}
                </Alert>
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={9} sx={{ 
              display: 'flex', 
              gap: 1, 
              justifyContent: isMobile ? 'flex-start' : 'flex-end', 
              flexWrap: 'wrap' 
            }}>
              <Tooltip title={tr('Yenile', 'Refresh')}>
                {/* MUI, devre dışı bir butonu Tooltip çocuğu olarak aldığında uyarır: sarmalayıcı gerekli. */}
                <span>
                  <IconButton
                    onClick={loadScreens}
                    disabled={refreshing}
                    size={isMobile ? 'small' : 'medium'}
                  >
                    <RefreshIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                color="primary"
                size={isMobile ? 'small' : 'medium'}
                startIcon={<AddIcon />}
                onClick={(e) => setAddMenuAnchorEl(e.currentTarget)}
              >
                {tr('Cihaz Ekle', 'Add Device')}
              </Button>
              <Menu
                anchorEl={addMenuAnchorEl}
                open={Boolean(addMenuAnchorEl)}
                onClose={() => setAddMenuAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem onClick={() => openAddModal('media')}>
                  <ListItemIcon>
                    <ImageIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText>{tr('Medya Ekranı', 'Media Screen')}</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => openAddModal('orders')} disabled={qrMenus.length === 0}>
                  <ListItemIcon>
                    <RestaurantIcon fontSize="small" color="secondary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={tr('Sipariş Ekranı', 'Order Screen')}
                    secondary={qrMenus.length === 0 ? tr('Önce QR menü tanımlayın', 'Define a QR menu first') : undefined}
                  />
                </MenuItem>
                <MenuItem onClick={() => openAddModal('kiosk')} disabled={kiosks.length === 0}>
                  <ListItemIcon>
                    <KioskIcon fontSize="small" sx={{ color: '#ff6f00' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={tr('Kiosk Ekranı', 'Kiosk Screen')}
                    secondary={kiosks.length === 0 ? tr('Önce kiosk tanımlayın', 'Define a kiosk first') : undefined}
                  />
                </MenuItem>
                <MenuItem onClick={() => openAddModal('accessControl')}>
                  <ListItemIcon>
                    <AccessControlIcon fontSize="small" sx={{ color: '#2e7d32' }} />
                  </ListItemIcon>
                  <ListItemText>{tr('Geçiş Kontrol Bilgisayarı', 'Access Control Computer')}</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => openAddModal('pdks')}>
                  <ListItemIcon>
                    <PdksIcon fontSize="small" sx={{ color: '#6a1b9a' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="PDKS"
                    secondary={tr('local-service için 11 haneli kod', '11-digit code for local-service')}
                  />
                </MenuItem>
                <MenuItem onClick={() => openAddModal('pavo')}>
                  <ListItemIcon>
                    <PavoIcon fontSize="small" sx={{ color: '#7c4dff' }} />
                  </ListItemIcon>
                  <ListItemText>{tr('PavoPay Cihazı', 'PavoPay Device')}</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => openAddModal('salesTerminal')}>
                  <ListItemIcon>
                    <SalesTerminalIcon fontSize="small" sx={{ color: '#546e7a' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={tr('Satış Ekranı Ekle', 'Add Sales Screen')}
                    secondary={tr('local-service için 11 haneli kod', '11-digit code for local-service')}
                  />
                </MenuItem>
              </Menu>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Ekranlar Tablosu — şube filtresi boşsa şirketin tüm cihazları listelenir.
          İlk boyamada refreshing henüz false olduğu için loading de kontrol edilir,
          aksi halde "cihaz yok" boş durumu bir an görünüp kaybolur. */}
      {loading || refreshing ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : screens.length > 0 ? (
          <>
            {/* Masaüstü Tablo Görünümü */}
            {!isMobile ? (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>{tr('Ekran Kodu', 'Screen Code')}</strong></TableCell>
                      <TableCell><strong>{tr('Ekran Adı', 'Screen Name')}</strong></TableCell>
                      <TableCell><strong>{tr('Tip', 'Type')}</strong></TableCell>
                      <TableCell><strong>{tr('Durum', 'Status')}</strong></TableCell>
                      <TableCell><strong>{tr('Cihaz Bilgisi', 'Device Info')}</strong></TableCell>
                      <TableCell><strong>{tr('Son Görülme', 'Last Seen')}</strong></TableCell>
                      <TableCell align="center"><strong>{tr('İşlemler', 'Actions')}</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {screens.map((screen) => (
                      <TableRow key={screen.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle2" fontWeight="bold" fontFamily="monospace">
                              {screen.screenCode}
                            </Typography>
                            <Tooltip title={tr('Kodu Kopyala', 'Copy Code')}>
                              <IconButton size="small" onClick={() => copyToClipboard(screen.screenCode)}>
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          {/* ✅ FIX: Pavo cihazlarinda backend Description'a sirket adi / "Bilinmeyen Sirket"
                              placeholder'i yaziyor — ekran kodu sutununda bunu gostermek yerine sadece
                              kodu birakalim. */}
                          {screen.screenType !== 'pavo' && screen.description && (
                            <Typography variant="caption" color="text.secondary">
                              {screen.description}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2">{screen.screenName}</Typography>
                          {screen.screenType === 'media' && screen.branchName && (
                            <Typography variant="caption" color="text.secondary">
                              📍 {screen.branchName}
                            </Typography>
                          )}
                          {screen.screenType === 'orders' && screen.qrMenuName && (
                            <Typography variant="caption" color="text.secondary">
                              🍽️ {screen.qrMenuName}
                            </Typography>
                          )}
                          {screen.screenType === 'kiosk' && screen.kioskId && (
                            <Typography variant="caption" color="text.secondary">
                              🖥️ {tr('Kiosk', 'Kiosk')} #{screen.kioskId}
                            </Typography>
                          )}
                          {screen.screenType === 'pavo' && screen.deviceMode && (
                            <Typography variant="caption" color="text.secondary">
                              💳 {screen.deviceMode === 'pavo_order' ? tr('Sipariş Modu', 'Order Mode') : screen.deviceMode === 'pavo_quicksale' ? tr('Hızlı Satış', 'Quick Sale') : tr('Restoran', 'Restaurant')}
                            </Typography>
                          )}
                          {screen.screenType === 'salesTerminal' && (
                            <Typography variant="caption" color="text.secondary">
                              🛒 {getSalesTerminalModeLabel(screen.deviceMode)}
                              {screen.linkedScreenId ? ` #${screen.linkedScreenId}` : ''}
                            </Typography>
                          )}
                          {(screen.screenType === 'accessControl' || screen.screenType === 'pdks') && screen.branchName && (
                            <Typography variant="caption" color="text.secondary">
                              🚪 {screen.branchName}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getScreenTypeMeta(screen.screenType).icon}
                            label={getScreenTypeMeta(screen.screenType).label}
                            color={getScreenTypeMeta(screen.screenType).color}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(screen.status) || undefined}
                            label={getStatusText(screen.status)}
                            color={getStatusColor(screen.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {screen.deviceModel ? (
                            <Box>
                              <Typography variant="body2">{screen.deviceModel}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Android {screen.androidVersion} • v{screen.appVersion}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              {tr('Henüz bağlanmadı', 'Not connected yet')}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {screen.lastSeen ? (
                            <Typography variant="caption">
                              {new Date(screen.lastSeen).toLocaleString('tr-TR')}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton size="small" onClick={(e) => handleMenuOpen(e, screen)}>
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              /* Mobil Kart Görünümü */
              <Grid container spacing={2}>
                {screens.map((screen) => (
                  <Grid item xs={12} key={screen.id}>
                    <Card 
                      sx={{ 
                        borderLeft: 4, 
                        borderColor: getScreenTypeMeta(screen.screenType).borderColor
                      }}
                    >
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        {/* Üst Satır: Tip + Durum + 3 Nokta */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              icon={getScreenTypeMeta(screen.screenType).icon}
                              label={getScreenTypeMeta(screen.screenType).label}
                              color={getScreenTypeMeta(screen.screenType).color}
                              size="small"
                              variant="outlined"
                            />
                            <Chip
                              icon={getStatusIcon(screen.status) || undefined}
                              label={getStatusText(screen.status)}
                              color={getStatusColor(screen.status)}
                              size="small"
                            />
                          </Box>
                          <IconButton size="small" onClick={(e) => handleMenuOpen(e, screen)}>
                            <MoreVertIcon />
                          </IconButton>
                        </Box>

                        {/* Ekran Adı */}
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
                          📺 {screen.screenName}
                        </Typography>

                        {/* Ekran Kodu + Kopyala */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                          <Typography variant="body2" fontFamily="monospace" color="text.secondary">
                            🔑 {screen.screenCode}
                          </Typography>
                          <IconButton size="small" onClick={() => copyToClipboard(screen.screenCode)}>
                            <ContentCopyIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>

                        {/* Alt Bilgi: Şube veya Menü */}
                        {screen.screenType === 'media' && screen.branchName && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, ml: 2.5 }}>
                            ↳ 📍 {screen.branchName}
                          </Typography>
                        )}
                        {screen.screenType === 'orders' && screen.qrMenuName && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, ml: 2.5 }}>
                            ↳ 🍽️ {screen.qrMenuName}
                          </Typography>
                        )}
                        {screen.screenType === 'pavo' && screen.deviceMode && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, ml: 2.5 }}>
                            ↳ 💳 {screen.deviceMode === 'pavo_order' ? tr('Sipariş Modu', 'Order Mode') : screen.deviceMode === 'pavo_quicksale' ? tr('Hızlı Satış', 'Quick Sale') : tr('Restoran', 'Restaurant')}
                          </Typography>
                        )}
                        {screen.screenType === 'salesTerminal' && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, ml: 2.5 }}>
                            ↳ 🛒 {getSalesTerminalModeLabel(screen.deviceMode)}
                            {screen.linkedScreenId ? ` #${screen.linkedScreenId}` : ''}
                          </Typography>
                        )}
                        {(screen.screenType === 'accessControl' || screen.screenType === 'pdks') && screen.branchName && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, ml: 2.5 }}>
                            ↳ 🚪 {screen.branchName}
                          </Typography>
                        )}

                        <Divider sx={{ my: 1.5 }} />

                        {/* 2x1 Grid: Cihaz Bilgisi + Son Görülme */}
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">{tr('Cihaz', 'Device')}</Typography>
                            {screen.deviceModel ? (
                              <Box>
                                <Typography variant="body2" fontWeight="medium">{screen.deviceModel}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Android {screen.androidVersion} • v{screen.appVersion}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                {tr('Henüz bağlanmadı', 'Not connected yet')}
                              </Typography>
                            )}
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">{tr('Son Görülme', 'Last Seen')}</Typography>
                            {screen.lastSeen ? (
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  {new Date(screen.lastSeen).toLocaleDateString('tr-TR')}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(screen.lastSeen).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">-</Typography>
                            )}
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}

            {/* 3 Nokta Menüsü (Masaüstü + Mobil ortak) */}
            <Menu
              anchorEl={menuAnchorEl}
              open={Boolean(menuAnchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {/* Düzenle - sadece media ve orders için (pavo ayrı API kullanır;
                  kiosk, geçiş kontrol bilgisayarı ve satış terminalinde
                  düzenlenecek ekran ayarı yok — openEditModal bu tipleri
                  karşılamıyor, listelenirse tıklama boşa gider) */}
              {selectedScreenForMenu && selectedScreenForMenu.screenType !== 'kiosk' && selectedScreenForMenu.screenType !== 'pavo' && selectedScreenForMenu.screenType !== 'accessControl' && selectedScreenForMenu.screenType !== 'pdks' && selectedScreenForMenu.screenType !== 'salesTerminal' && (
                <MenuItem 
                  onClick={() => {
                    openEditModal(selectedScreenForMenu!);
                    handleMenuClose();
                  }}
                >
                  <ListItemIcon>
                    <EditIcon fontSize="small" color="info" />
                  </ListItemIcon>
                  <ListItemText>{tr('Düzenle', 'Edit')}</ListItemText>
                </MenuItem>
              )}
              {selectedScreenForMenu?.screenType === 'media' && (
                <MenuItem 
                  onClick={() => {
                    console.log('[DeviceMgmt] Medya modali aciliyor - screen:', selectedScreenForMenu?.id, 'screenCode:', selectedScreenForMenu?.screenCode, 'screenName:', selectedScreenForMenu?.screenName);
                    setSelectedScreen(selectedScreenForMenu);
                    setMediaModalOpen(true);
                    handleMenuClose();
                  }}
                >
                  <ListItemIcon>
                    <PhotoLibraryIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText>{tr('Medya Yönetimi', 'Media Management')}</ListItemText>
                </MenuItem>
              )}
              {selectedScreenForMenu?.screenType === 'orders' && (
                <MenuItem 
                  onClick={() => {
                    window.open(`#/musteri-ekrani/${selectedScreenForMenu.qrMenuId}`, '_blank');
                    handleMenuClose();
                  }}
                >
                  <ListItemIcon>
                    <TvIcon fontSize="small" color="secondary" />
                  </ListItemIcon>
                  <ListItemText>{tr('Ekranı Aç', 'Open Screen')}</ListItemText>
                </MenuItem>
              )}
              <Divider />
              <MenuItem 
                onClick={() => {
                  if (selectedScreenForMenu) {
                    setSelectedScreen(selectedScreenForMenu);
                    setDeleteModalOpen(true);
                  }
                  handleMenuClose();
                }}
                sx={{ color: 'error.main' }}
              >
                <ListItemIcon>
                  <DeleteIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText>{tr('Sil', 'Delete')}</ListItemText>
              </MenuItem>
            </Menu>
          </>
      ) : (
        <Paper sx={{ p: 8, textAlign: 'center' }}>
          <TvIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {selectedBranchId
              ? tr('Bu şubede henüz cihaz eklenmemiş', 'No devices have been added in this branch yet')
              : tr('Henüz cihaz eklenmemiş', 'No devices have been added yet')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {tr('İlk cihazınızı eklemek için "Cihaz Ekle" butonunu kullanın.', 'Use the "Add Device" button to add your first device.')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={(e) => setAddMenuAnchorEl(e.currentTarget)}
          >
            {tr('Cihaz Ekle', 'Add Device')}
          </Button>
        </Paper>
      )}

      {/* Medya Ekranı Ekle / Düzenle Modal */}
      <Dialog open={addModalOpen} onClose={() => !saving && resetFormAndCloseModals()} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ImageIcon color="primary" />
              <Typography variant="h6">{editingScreen ? tr('Medya Ekranını Düzenle', 'Edit Media Screen') : tr('Yeni Medya Ekranı Ekle', 'Add New Media Screen')}</Typography>
            </Box>
            <IconButton edge="end" onClick={() => resetFormAndCloseModals()} disabled={saving}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {!editingScreen && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  {tr('Medya ekranına özel bir kod otomatik olarak oluşturulacak. Bu kodu Flutter uygulamasında kurulum sırasında gireceksiniz.', 'A unique code will be generated automatically for the media screen. Enter this code during setup in the Flutter app.')}
                </Alert>
              </Grid>
            )}
            {editingScreen && (
              <Grid item xs={12}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {tr('Ekran Kodu', 'Screen Code')}: <strong>{editingScreen.screenCode}</strong> — {tr('Kod değiştirilemez.', 'Code cannot be changed.')}
                </Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              {renderFormBranchField()}
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={tr('Ekran Adı *', 'Screen Name *')}
                value={screenName}
                onChange={(e) => setScreenName(e.target.value)}
                placeholder={tr('Örn: Giriş Kapısı Ekranı', 'e.g. Entrance Screen')}
                disabled={saving}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={tr('Açıklama', 'Description')}
                value={screenDescription}
                onChange={(e) => setScreenDescription(e.target.value)}
                placeholder={tr('Ekran hakkında kısa açıklama', 'Short description about the screen')}
                multiline
                rows={3}
                disabled={saving}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth disabled={saving}>
                <InputLabel>{tr('Ekran Yönü', 'Screen Orientation')}</InputLabel>
                <Select
                  value={screenOrientation}
                  label={tr('Ekran Yönü', 'Screen Orientation')}
                  onChange={(e) => setScreenOrientation(e.target.value)}
                  startAdornment={<ScreenRotationIcon sx={{ mr: 1, color: 'action.active' }} />}
                >
                  <MenuItem value="landscape">{tr('Yatay (Landscape)', 'Landscape')}</MenuItem>
                  <MenuItem value="portrait-left">{tr('Dikey Sol (Portrait Left)', 'Portrait Left')}</MenuItem>
                  <MenuItem value="portrait-right">{tr('Dikey Sağ (Portrait Right)', 'Portrait Right')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => resetFormAndCloseModals()} disabled={saving}>
            {tr('İptal', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={editingScreen ? handleUpdateScreen : handleAddScreen}
            disabled={saving || !screenName.trim()}
            startIcon={saving ? <CircularProgress size={20} /> : editingScreen ? <EditIcon /> : <AddIcon />}
          >
            {editingScreen ? tr('Güncelle', 'Update') : tr('Ekle', 'Add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sipariş Ekranı Ekle / Düzenle Modal */}
      <Dialog open={addOrdersModalOpen} onClose={() => !saving && resetFormAndCloseModals()} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <RestaurantIcon color="secondary" />
              <Typography variant="h6">{editingScreen ? tr('Sipariş Ekranını Düzenle', 'Edit Order Screen') : tr('Yeni Sipariş Ekranı Ekle', 'Add New Order Screen')}</Typography>
            </Box>
            <IconButton edge="end" onClick={() => resetFormAndCloseModals()} disabled={saving}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {!editingScreen && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  {tr('Sipariş ekranına özel bir kod otomatik olarak oluşturulacak. Bu kod ile Android TV\'de sipariş takip ekranı açılacaktır.', 'A unique code will be generated automatically for the order screen. This code will open the order tracking screen on Android TV.')}
                </Alert>
              </Grid>
            )}
            {editingScreen && (
              <Grid item xs={12}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {tr('Ekran Kodu', 'Screen Code')}: <strong>{editingScreen.screenCode}</strong> — {tr('Kod değiştirilemez.', 'Code cannot be changed.')}
                </Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControl fullWidth disabled={saving}>
                <InputLabel>{tr('QR Menü Seçin *', 'Select QR Menu *')}</InputLabel>
                <Select
                  value={selectedMenuId}
                  label={tr('QR Menü Seçin *', 'Select QR Menu *')}
                  onChange={(e) => setSelectedMenuId(e.target.value)}
                >
                  {qrMenus.map((menu) => (
                    <MenuItem key={menu.id} value={menu.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <RestaurantIcon fontSize="small" color="secondary" />
                        {menu.name}
                        {menu.branchName && (
                          <Typography variant="caption" color="text.secondary">
                            ({menu.branchName})
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {qrMenus.length === 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {tr('Bu şubede henüz QR menü tanımlanmamış. Lütfen önce bir menü oluşturun.', 'No QR menu has been defined for this branch yet. Please create a menu first.')}
                </Alert>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={tr('Ekran Adı *', 'Screen Name *')}
                value={screenName}
                onChange={(e) => setScreenName(e.target.value)}
                placeholder={tr('Örn: Kasa Sipariş Ekranı 1', 'e.g. Cashier Order Screen 1')}
                disabled={saving}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={tr('Açıklama', 'Description')}
                value={screenDescription}
                onChange={(e) => setScreenDescription(e.target.value)}
                placeholder={tr('Ekran hakkında kısa açıklama', 'Short description about the screen')}
                multiline
                rows={3}
                disabled={saving}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth disabled={saving}>
                <InputLabel>{tr('Ekran Yönü', 'Screen Orientation')}</InputLabel>
                <Select
                  value={screenOrientation}
                  label={tr('Ekran Yönü', 'Screen Orientation')}
                  onChange={(e) => setScreenOrientation(e.target.value)}
                  startAdornment={<ScreenRotationIcon sx={{ mr: 1, color: 'action.active' }} />}
                >
                  <MenuItem value="landscape">{tr('Yatay (Landscape)', 'Landscape')}</MenuItem>
                  <MenuItem value="portrait-left">{tr('Dikey Sol (Portrait Left)', 'Portrait Left')}</MenuItem>
                  <MenuItem value="portrait-right">{tr('Dikey Sağ (Portrait Right)', 'Portrait Right')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => resetFormAndCloseModals()} disabled={saving}>
            {tr('İptal', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={editingScreen ? handleUpdateScreen : handleAddOrdersScreen}
            disabled={saving || (!editingScreen && !selectedMenuId) || !screenName.trim()}
            startIcon={saving ? <CircularProgress size={20} /> : editingScreen ? <EditIcon /> : <AddIcon />}
          >
            {editingScreen ? tr('Güncelle', 'Update') : tr('Ekle', 'Add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Kiosk Ekranı Ekle Modal */}
      <Dialog open={addKioskModalOpen} onClose={() => !saving && setAddKioskModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <KioskIcon sx={{ color: '#ff6f00' }} />
              <Typography variant="h6">{tr('Yeni Kiosk Ekranı Ekle', 'Add New Kiosk Screen')}</Typography>
            </Box>
            <IconButton edge="end" onClick={() => setAddKioskModalOpen(false)} disabled={saving}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                {tr('Kiosk cihazına özel bir 11 haneli kod otomatik olarak oluşturulacak. Bu kod ile kiosk uygulamasında giriş yapılabilir.', 'A unique 11-digit code will be generated automatically for the kiosk device. You can log in to the kiosk app using this code.')}
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth disabled={saving}>
                <InputLabel>{tr('Kiosk Seçin *', 'Select Kiosk *')}</InputLabel>
                <Select
                  value={selectedKioskId}
                  label={tr('Kiosk Seçin *', 'Select Kiosk *')}
                  onChange={(e) => {
                    setSelectedKioskId(e.target.value);
                    const kiosk = kiosks.find(k => k.id.toString() === e.target.value);
                    if (kiosk && !screenName.trim()) {
                      setScreenName(`${kiosk.name} ${tr('Cihaz', 'Device')}`);
                    }
                  }}
                >
                  {kiosks.map((kiosk) => (
                    <MenuItem key={kiosk.id} value={kiosk.id.toString()}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <KioskIcon fontSize="small" sx={{ color: '#ff6f00' }} />
                        {kiosk.name}
                        {kiosk.branchName && (
                          <Typography variant="caption" color="text.secondary">
                            ({kiosk.branchName})
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {kiosks.length === 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {tr('Henüz kiosk tanımlanmamış. Lütfen önce Kiosk Menü Yönetimi sayfasından bir kiosk oluşturun.', 'No kiosk has been defined yet. Please create a kiosk first from the Kiosk Menu Management page.')}
                </Alert>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={tr('Ekran Adı *', 'Screen Name *')}
                value={screenName}
                onChange={(e) => setScreenName(e.target.value)}
                placeholder={tr('Örn: Giriş Kiosk Cihazı', 'e.g. Entrance Kiosk Device')}
                disabled={saving}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={tr('Açıklama', 'Description')}
                value={screenDescription}
                onChange={(e) => setScreenDescription(e.target.value)}
                placeholder={tr('Cihaz hakkında kısa açıklama', 'Short description about the device')}
                multiline
                rows={3}
                disabled={saving}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddKioskModalOpen(false)} disabled={saving}>
            {tr('İptal', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleAddKioskScreen}
            disabled={saving || !selectedKioskId || !screenName.trim()}
            startIcon={saving ? <CircularProgress size={20} /> : <AddIcon />}
            sx={{ bgcolor: '#ff6f00', '&:hover': { bgcolor: '#e65100' } }}
          >
            {tr('Ekle', 'Add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Geçiş Kontrol Bilgisayarı Ekle Modal */}
      <Dialog open={addAccessControlModalOpen} onClose={() => !saving && setAddAccessControlModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessControlIcon sx={{ color: '#2e7d32' }} />
              <Typography variant="h6">{tr('Yeni Geçiş Kontrol Bilgisayarı', 'New Access Control Computer')}</Typography>
            </Box>
            <IconButton edge="end" onClick={() => setAddAccessControlModalOpen(false)} disabled={saving}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                {tr(
                  'Fabrikadaki panel bilgisayarı için 11 haneli kod otomatik oluşturulacak. Bu kod, local-service üzerinde Seçenekler > Geçiş Yönetimi sayfasına girilir; kod doğrulanmadan hiçbir kapı sunucuya bildirilemez.',
                  'An 11-digit code will be generated for the panel computer. Enter it in local-service under Options > Access Management; no door can be reported to the server before the code is verified.'
                )}
              </Alert>
            </Grid>
            <Grid item xs={12}>
              {renderFormBranchField()}
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={tr('Cihaz Adı *', 'Device Name *')}
                value={screenName}
                onChange={(e) => setScreenName(e.target.value)}
                placeholder={tr('Örn: Üretim Girişi Panel Bilgisayarı', 'e.g. Production Entrance Panel Computer')}
                disabled={saving}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={tr('Açıklama', 'Description')}
                value={screenDescription}
                onChange={(e) => setScreenDescription(e.target.value)}
                placeholder={tr('Cihazın bulunduğu yer, bağlı paneller vb.', 'Location of the device, connected panels, etc.')}
                multiline
                rows={3}
                disabled={saving}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddAccessControlModalOpen(false)} disabled={saving}>
            {tr('İptal', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleAddAccessControlScreen}
            disabled={saving || !screenName.trim()}
            startIcon={saving ? <CircularProgress size={20} /> : <AddIcon />}
            sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
          >
            {tr('Ekle', 'Add')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addPdksModalOpen} onClose={() => !saving && setAddPdksModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PdksIcon sx={{ color: '#6a1b9a' }} />
              <Typography variant="h6">{tr('Yeni PDKS Bilgisayarı', 'New PDKS Computer')}</Typography>
            </Box>
            <IconButton edge="end" onClick={() => setAddPdksModalOpen(false)} disabled={saving}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                {tr(
                  '11 haneli kod otomatik oluşur. local-service üzerinde POST /api/pdks/pair ile girilir; kod doğrulanmadan terminal bildirilemez.',
                  'An 11-digit code is generated. Enter it in local-service via POST /api/pdks/pair; terminals cannot be reported before the code is verified.'
                )}
              </Alert>
            </Grid>
            <Grid item xs={12}>{renderFormBranchField()}</Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={tr('Cihaz Adı *', 'Device Name *')}
                value={screenName}
                onChange={(e) => setScreenName(e.target.value)}
                placeholder={tr('Örn: Giriş Holü PDKS', 'e.g. Lobby PDKS')}
                disabled={saving}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={tr('Açıklama', 'Description')}
                value={screenDescription}
                onChange={(e) => setScreenDescription(e.target.value)}
                multiline
                rows={3}
                disabled={saving}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddPdksModalOpen(false)} disabled={saving}>{tr('İptal', 'Cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleAddPdksScreen}
            disabled={saving || !screenName.trim()}
            startIcon={saving ? <CircularProgress size={20} /> : <AddIcon />}
            sx={{ bgcolor: '#6a1b9a', '&:hover': { bgcolor: '#4a148c' } }}
          >
            {tr('Ekle', 'Add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* PavoPay Cihaz Ekle Modal */}
      <Dialog open={addPavoModalOpen} onClose={() => !saving && setAddPavoModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PavoIcon sx={{ color: '#7c4dff' }} />
              <Typography variant="h6">{tr('Yeni PavoPay Cihaz Ekle', 'Add New PavoPay Device')}</Typography>
            </Box>
            <IconButton edge="end" onClick={() => setAddPavoModalOpen(false)} disabled={saving}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                {tr('PavoPay cihazı için 11 haneli aktivasyon kodu otomatik oluşturulacak. Bu kodu Flutter uygulamasında giriş yaparak cihazı aktive edebilirsiniz.', 'An 11-digit activation code will be generated automatically for the PavoPay device. You can activate the device by logging in via the Flutter app with this code.')}
              </Alert>
            </Grid>
            <Grid item xs={12}>
              {renderFormBranchField()}
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={tr('Cihaz Adı *', 'Device Name *')}
                value={pavoDeviceName}
                onChange={(e) => setPavoDeviceName(e.target.value)}
                placeholder={tr('Örn: Kasa 1 PavoPay', 'e.g. Cashier 1 PavoPay')}
                disabled={saving}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth disabled={saving}>
                <InputLabel>{tr('Cihaz Modu *', 'Device Mode *')}</InputLabel>
                <Select
                  value={pavoDeviceMode}
                  label={tr('Cihaz Modu *', 'Device Mode *')}
                  onChange={(e) => setPavoDeviceMode(e.target.value)}
                >
                  <MenuItem value="pavo_order">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <RestaurantIcon fontSize="small" color="secondary" />
                      {tr('Sipariş Modu', 'Order Mode')}
                    </Box>
                  </MenuItem>
                  <MenuItem value="pavo_quicksale">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PavoIcon fontSize="small" color="primary" />
                      {tr('Hızlı Satış Modu', 'Quick Sale Mode')}
                    </Box>
                  </MenuItem>
                  <MenuItem value="pavo_restaurant">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <RestaurantIcon fontSize="small" color="warning" />
                      {tr('Restoran Modu', 'Restaurant Mode')}
                    </Box>
                  </MenuItem>
                  <MenuItem value="pavo_rest_p2p">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PavoIcon fontSize="small" color="success" />
                      {tr('Rest-P2P Modu', 'Rest-P2P Mode')}
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Hizli Satis Ekrani Secimi */}
            {pavoDeviceMode === 'pavo_quicksale' && (
              <Grid item xs={12}>
                <FormControl fullWidth disabled={saving || loadingScreensForPavo}>
                  <InputLabel>{tr('Hızlı Satış Ekranı *', 'Quick Sale Screen *')}</InputLabel>
                  <Select
                    value={pavoLinkedScreenId}
                    label={tr('Hızlı Satış Ekranı *', 'Quick Sale Screen *')}
                    onChange={(e) => setPavoLinkedScreenId(Number(e.target.value) || '')}
                  >
                    {quickSaleScreensForPavo.length === 0 && !loadingScreensForPavo && (
                      <MenuItem value="" disabled>
                        {tr('Bu şubeye ait aktif hızlı satış ekranı yok', 'There is no active quick sale screen for this branch')}
                      </MenuItem>
                    )}
                    {quickSaleScreensForPavo.map((s) => (
                      <MenuItem key={s.Id} value={s.Id}>
                        {s.Name}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1 }}>
                    {tr('Bu cihaz hangi hızlı satış ekranının ayarlarını kullansın?', 'Which quick sale screen settings should this device use?')}
                  </Typography>
                </FormControl>
              </Grid>
            )}

            {/* Restoran Ekrani Secimi */}
            {pavoDeviceMode === 'pavo_restaurant' && (
              <Grid item xs={12}>
                <FormControl fullWidth disabled={saving || loadingScreensForPavo}>
                  <InputLabel>{tr('Restoran Ekranı *', 'Restaurant Screen *')}</InputLabel>
                  <Select
                    value={pavoLinkedScreenId}
                    label={tr('Restoran Ekranı *', 'Restaurant Screen *')}
                    onChange={(e) => setPavoLinkedScreenId(Number(e.target.value) || '')}
                  >
                    {restaurantScreensForPavo.length === 0 && !loadingScreensForPavo && (
                      <MenuItem value="" disabled>
                        {tr('Bu şubeye ait aktif restoran ekranı yok', 'There is no active restaurant screen for this branch')}
                      </MenuItem>
                    )}
                    {restaurantScreensForPavo.map((s) => (
                      <MenuItem key={s.Id} value={s.Id}>
                        {s.Name}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1 }}>
                    {tr('Bu cihaz hangi restoran ekranının ayarlarını kullansın?', 'Which restaurant screen settings should this device use?')}
                  </Typography>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddPavoModalOpen(false)} disabled={saving}>
            {tr('İptal', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleAddPavoScreen}
            disabled={
              saving ||
              !pavoDeviceName.trim() ||
              ((pavoDeviceMode === 'pavo_quicksale' || pavoDeviceMode === 'pavo_restaurant') && !pavoLinkedScreenId)
            }
            startIcon={saving ? <CircularProgress size={20} /> : <AddIcon />}
            sx={{ bgcolor: '#7c4dff', '&:hover': { bgcolor: '#651fff' } }}
          >
            {tr('Ekle', 'Add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Satış Ekranı Kodu Üret Modal — local-service bu kodla ekranı personel
          girişi olmadan açar (bkz. TerminalSessionBootstrap). */}
      <Dialog
        open={addSalesTerminalModalOpen}
        onClose={() => !saving && resetFormAndCloseModals()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SalesTerminalIcon sx={{ color: '#546e7a' }} />
              <Typography variant="h6">{tr('Satış Ekranı Kodu Üret', 'Generate Sales Screen Code')}</Typography>
            </Box>
            <IconButton edge="end" onClick={() => resetFormAndCloseModals()} disabled={saving}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert severity="info">
                {tr(
                  '11 haneli kod otomatik üretilir. Kodu local-service üzerindeki "Satış Ekranı Bağla" sayfasına girdiğinizde, seçtiğiniz ekran personel girişi olmadan açılır. Cihazın şubesi bağladığınız ekrandan devralınır.',
                  'An 11-digit code is generated automatically. When you enter it on the "Link Sales Screen" page in local-service, the selected screen opens without a personnel login. The device inherits the branch of the screen you link.'
                )}
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={tr('Cihaz Adı *', 'Device Name *')}
                value={salesTerminalName}
                onChange={(e) => setSalesTerminalName(e.target.value)}
                placeholder={tr('Örn: Kasa 1 Bilgisayarı', 'e.g. Cashier 1 Computer')}
                disabled={saving}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth disabled={saving}>
                <InputLabel>{tr('Hedef Mod *', 'Target Mode *')}</InputLabel>
                <Select
                  value={salesTerminalMode}
                  label={tr('Hedef Mod *', 'Target Mode *')}
                  onChange={(e) => setSalesTerminalMode(e.target.value as TerminalDeviceMode)}
                >
                  <MenuItem value="sales_quicksale">{tr('Hızlı Satış', 'Quick Sale')}</MenuItem>
                  <MenuItem value="sales_restaurant">{tr('Restoran', 'Restaurant')}</MenuItem>
                  <MenuItem value="sales_package">{tr('Paket Sipariş', 'Package Order')}</MenuItem>
                  <MenuItem value="sales_pyk">{tr('PYK', 'PYK')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth disabled={saving || loadingSalesTerminalScreens}>
                <InputLabel>{tr('Bağlanacak Ekran *', 'Screen To Link *')}</InputLabel>
                <Select
                  value={salesTerminalLinkedScreenId}
                  label={tr('Bağlanacak Ekran *', 'Screen To Link *')}
                  onChange={(e) => setSalesTerminalLinkedScreenId(Number(e.target.value) || '')}
                >
                  {salesTerminalScreenOptions.length === 0 && !loadingSalesTerminalScreens && (
                    <MenuItem value="" disabled>
                      {tr('Bu mod için aktif ekran bulunamadı', 'No active screen found for this mode')}
                    </MenuItem>
                  )}
                  {salesTerminalScreenOptions.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1 }}>
                  {loadingSalesTerminalScreens
                    ? tr('Ekranlar yükleniyor...', 'Loading screens...')
                    : tr('Kod girildiğinde bu ekran açılacak.', 'This screen will open when the code is entered.')}
                </Typography>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => resetFormAndCloseModals()} disabled={saving}>
            {tr('İptal', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleAddSalesTerminal}
            disabled={saving || !salesTerminalName.trim() || !salesTerminalLinkedScreenId}
            startIcon={saving ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {tr('Kod Üret', 'Generate Code')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Silme Onay Modal */}
      <Dialog open={deleteModalOpen} onClose={() => !saving && setDeleteModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
            <DeleteIcon />
            <Typography variant="h6">TV Ekranını Kalıcı Olarak Sil</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" gutterBottom>
              <strong>{selectedScreen?.screenName}</strong> ({selectedScreen?.screenCode}) ekranını silmek üzeresiniz.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Bu işlem şunları içerir:
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 2 }}>
              <li>
                <Typography variant="body2" color="text.secondary">
                  TV ekranı kaydı veritabanından kalıcı olarak silinecek
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="text.secondary">
                  Ekrana yüklenmiş tüm medya ayarları silinecek
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="text.secondary">
                  Firebase Storage'daki medya dosyaları korunacak (manuel temizleme gerekebilir)
                </Typography>
              </li>
            </Box>
          </Box>
          <Alert severity="error" icon={<DeleteIcon />} sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight="bold">
              ⚠️ BU İŞLEM GERİ ALINAMAZ!
            </Typography>
            <Typography variant="caption">
              Silinen ekran bilgileri kurtarılamaz. Devam etmek istediğinizden emin misiniz?
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteModalOpen(false)} disabled={saving} size="large">
            İptal
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteScreen}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <DeleteIcon />}
            size="large"
          >
            {saving ? 'Siliniyor...' : 'Evet, Kalıcı Olarak Sil'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Medya Yönetimi Modal */}
      {selectedScreen && (
        <MediaManagementModal
          open={mediaModalOpen}
          onClose={() => {
            setMediaModalOpen(false);
            setSelectedScreen(null);
          }}
          screenId={selectedScreen.id}
          screenCode={selectedScreen.screenCode}
          screenName={selectedScreen.screenName}
          currentMediaSettings={selectedScreen.mediaSettings}
          onSave={() => {
            showSnackbar('Medya ayarları başarıyla kaydedildi!', 'success');
            loadScreens();
          }}
        />
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default DeviceManagement;
